// == FOOTER ============================================================
import React from "react";
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import packageJson from "../../package.json";

export default function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="relative">
      {/* Fond principal */}
      <div className="relative w-full bg-[#FFF4D8] border-t border-[#E9D8A6] backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          {/* Ligne du haut : logo + nav + newsletter */}
          <div className="grid gap-10 md:gap-12 lg:grid-cols-3">
            {/* Bloc marque */}
            <div>
              <p className="mt-4 max-w-sm text-[#3A3A3A]/80">
                {t("footer.brand.tagline")}
              </p>

              {/* Coordonnées */}
              <ul className="mt-4 space-y-2 text-sm text-[#3A3A3A]/80">
                <li className="flex items-center gap-2">
                  <MapPinIcon className="h-4 w-4" />
                  {t("footer.contact.location")}
                </li>
                <li className="flex items-center gap-2">
                  <EnvelopeIcon className="h-4 w-4" />
                  <a
                    href="mailto:information@ananassolutions.com"
                    className="underline"
                    aria-label={t("footer.contact.emailAria")}
                  >
                    information@ananassolutions.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <PhoneIcon className="h-4 w-4" />
                  <a
                    href="tel:+18557001721"
                    className="underline"
                    aria-label={t("footer.contact.phoneAria")}
                  >
                    1-855-700-1721
                  </a>
                </li>
              </ul>
            </div>

            {/* Navigation */}
            <div className="grid grid-cols-2 gap-8 sm:gap-12 justify-center items-start w-fit mx-auto md:mx-0 md:justify-start">
              <nav className="min-w-[160px]">
                <h3 className="text-sm font-semibold tracking-wide text-[#3A3A3A]/70">
                  {t("footer.nav.explore.title")}
                </h3>
                <ul className="mt-3 space-y-2 text-[#3A3A3A]">
                  <li>
                    <a className="hover:underline" href="/boutique">
                      {t("footer.nav.explore.shopLocal")}
                    </a>
                  </li>
                  <li>
                    <a className="hover:underline" href="/creer-evenement">
                      {t("footer.nav.explore.createEvent")}
                    </a>
                  </li>
                  <li>
                    <a className="hover:underline" href="/entreprises">
                      {t("footer.nav.explore.localBusinesses")}
                    </a>
                  </li>
                  <li>
                    <a className="hover:underline" href="/evenements-a-venir">
                      {t("footer.nav.explore.upcomingEvents")}
                    </a>
                  </li>
                  <li>
                    <a className="hover:underline hidden" href="/blog">
                      {t("footer.nav.explore.news")}
                    </a>
                  </li>
                </ul>
              </nav>

              <nav className="min-w-[160px]">
                <h3 className="text-sm font-semibold tracking-wide text-[#3A3A3A]/70">
                  {t("footer.nav.resources.title")}
                </h3>
                <ul className="mt-3 space-y-2 text-[#3A3A3A]">
                  <li>
                    <a className="hover:underline" href="/a-propos">
                      {t("footer.nav.resources.about")}
                    </a>
                  </li>
                  <li>
                    <a className="hover:underline" href="/devenir-partenaire">
                      {t("footer.nav.resources.becomePartner")}
                    </a>
                  </li>
                  <li>
                    <a className="hover:underline" href="/contact">
                      {t("footer.nav.resources.contact")}
                    </a>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Newsletter (désactivée comme dans ton code d'origine) */}
            <div className="hidden">
              <h3 className="text-sm font-semibold tracking-wide text-[#3A3A3A]/70">
                {t("footer.newsletter.title")}
              </h3>
              <p className="mt-3 text-[#3A3A3A]/80">
                {t("footer.newsletter.subtitle")}
              </p>
              <form
                className="mt-4 flex items-center gap-2"
                onSubmit={(e) => e.preventDefault()}
              >
                <input
                  type="email"
                  placeholder={t("footer.newsletter.placeholderEmail")}
                  className="w-full rounded-full border border-black/10 bg-white/90 px-4 py-3 text-[#3A3A3A] placeholder-[#3A3A3A]/50 focus:outline-none focus:ring-2 focus:ring-[#F3DA77]"
                />
                <button
                  type="submit"
                  className="shrink-0 rounded-full bg-[#FFD76A] px-5 py-3 font-semibold text-[#3A3A3A] shadow-sm hover:bg-[#F3DA77]"
                >
                  {t("footer.newsletter.ctaSubscribe")}
                </button>
              </form>

              {/* Langue (même ton que la navbar) */}
              <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#3A3A3A]/80">
                <GlobeAltIcon className="h-4 w-4" />
                {t("footer.newsletter.langShort")}
              </div>
            </div>
          </div>

          {/* Ligne du bas */}
          <div className="mt-10 border-t border-black/10 pt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between text-sm text-[#3A3A3A]/70 text-center md:text-left">
            <p>
              {t("footer.bottom.copyright", { year: new Date().getFullYear() })}
            </p>
            <span className="opacity-40">{t("footer.bottom.separator")}</span>
            <p>
              {t("footer.bottom.version", { version: packageJson.version })}
            </p>

            {/* Liens: centrés en mobile, à DROITE en desktop */}
            <div className="flex flex-wrap items-center justify-center md:justify-end md:ml-auto gap-4">
              <a className="hover:underline" href="/terms-and-conditions">
                {t("footer.bottom.terms")}
              </a>
              <span className="opacity-40">{t("footer.bottom.separator")}</span>
              <a className="hover:underline" href="/privacy">
                {t("footer.bottom.privacy")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
